import { Injectable, effect, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { AuthService } from '../auth/auth.service';
import {
  NavigationDropdown,
  NavigationItem,
  NavigationLink
} from './navigation-item.interface';

import {
  Empresa,
  abrirVentanaAsistente
} from '../../layouts/components/asistente-burbuja/asistente-chat.service';

/**
 * Menú lateral de Invertus CRM.
 *
 * Organizado por EMPRESA (Invering / Infinitus) según feedback David 24/04/2026.
 * El menú se filtra por los MÓDULOS del rol del usuario (`auth.tieneModulo`):
 * cada sección/ítem aparece solo si el rol le da acceso. Un admin (`'*'`) ve
 * todo. Se recomputa reactivamente en cada cambio de sesión (login/logout).
 *
 * OJO: esto es filtrado de UI, no control de acceso — el backend revalida cada
 * request contra la BD (`require_modulo`).
 */
@Injectable({
  providedIn: 'root'
})
export class NavigationLoaderService {
  private readonly auth = inject(AuthService);

  private readonly _items: BehaviorSubject<NavigationItem[]> =
    new BehaviorSubject<NavigationItem[]>([]);

  get items$(): Observable<NavigationItem[]> {
    return this._items.asObservable();
  }

  constructor() {
    // Recomputa el menú cuando cambia el usuario (login/logout). El effect
    // lee el signal `user()` a través de `tieneModulo`, así que se re-dispara
    // solo al cambiar la sesión.
    effect(() => {
      this.auth.user();
      this.loadNavigation();
    });
  }

  /** Abre el asistente dedicado en su ventana (ver `abrirVentanaAsistente`). */
  abrirAsistente(empresa: Empresa): void {
    abrirVentanaAsistente(empresa);
  }

  loadNavigation(): void {
    const has = (modulo: string): boolean => this.auth.tieneModulo(modulo);

    const badgePronto = {
      value: 'Pronto',
      bgClass: 'bg-gray-200',
      textClass: 'text-gray-700'
    };

    const items: NavigationItem[] = [];

    // ── Inicio ──────────────────────────────────────────────────────────
    if (has('dashboard')) {
      items.push({
        type: 'subheading',
        label: 'Inicio',
        children: [
          {
            type: 'link',
            label: 'Dashboard',
            route: '/',
            icon: 'mat:home',
            routerLinkActiveOptions: { exact: true }
          }
        ]
      });
    }

    // ── Invering ────────────────────────────────────────────────────────
    const invering: Array<NavigationLink | NavigationDropdown> = [];
    if (has('proyectos')) {
      invering.push(
        {
          type: 'dropdown',
          label: 'Proyectos',
          icon: 'mat:description',
          children: [
            { type: 'link', label: 'Contratos', route: '/apps/proyectos/contratos' },
            { type: 'link', label: 'Tareas', route: '/apps/proyectos/tareas' }
          ]
        },
        {
          type: 'link',
          label: 'Compras',
          route: '/coming-soon',
          icon: 'mat:shopping_cart',
          badge: badgePronto
        }
      );
    }
    if (has('gestion_humana')) {
      invering.push({
        type: 'dropdown',
        label: 'Gestión Humana',
        icon: 'mat:groups',
        children: [
          { type: 'link', label: 'Requisiciones', route: '/apps/gh/requisiciones' },
          { type: 'link', label: 'Selección y reclutamiento', route: '/apps/gh/seleccion' },
          { type: 'link', label: 'Contratación', route: '/apps/gh/contratacion' },
          { type: 'link', label: 'Inducción y Dotación', route: '/apps/gh/induccion' },
          { type: 'link', label: 'Predicción de deserción', route: '/apps/gh/desercion' },
          { type: 'link', label: 'Publicar convocatorias', route: '/apps/gh/convocatorias' },
          {
            type: 'link',
            label: 'Convocatoria (postulaciones web)',
            route: '/apps/gh/seleccion/convocatoria'
          }
        ]
      });
    }
    if (has('community_invering')) {
      invering.push({
        type: 'dropdown',
        label: 'Community Manager',
        icon: 'mat:campaign',
        children: [
          { type: 'link', label: 'Bandeja', route: '/apps/community/invering/bandeja' },
          { type: 'link', label: 'Campañas', route: '/apps/community/invering/campanas' },
          { type: 'link', label: 'Plantillas', route: '/apps/community/invering/plantillas' },
          { type: 'link', label: 'Generador IA', route: '/apps/community/invering/generador' },
          { type: 'link', label: 'Calendario', route: '/apps/community/invering/calendario' },
          { type: 'link', label: 'Publicaciones', route: '/apps/community/invering/publicaciones' },
          { type: 'link', label: 'Informe del mes', route: '/apps/community/invering/informe-mensual' },
          { type: 'link', label: 'Reportes', route: '/apps/community/invering/reportes' },
          { type: 'link', label: 'Tareas', route: '/apps/community/invering/tareas' }
        ]
      });
    }
    if (has('helisa')) {
      invering.push({
        type: 'dropdown',
        label: 'Helisa (ERP)',
        icon: 'mat:account_balance',
        children: [
          { type: 'link', label: 'Resumen', route: '/apps/invering/resumen' },
          { type: 'link', label: 'Terceros (clientes + proveedores)', route: '/apps/invering/terceros' },
          { type: 'link', label: 'Empleados', route: '/apps/invering/empleados' }
        ]
      });
    }
    if (invering.length) {
      items.push({ type: 'subheading', label: 'Invering', children: invering });
    }

    // ── Infinitus ───────────────────────────────────────────────────────
    const infinitus: Array<NavigationLink | NavigationDropdown> = [];
    // COMERCIAL (pedido de Andrés 26/10/2026, punto 5): lo que antes eran links
    // planos ahora es un grupo. Orden según lo pidió él: inventario primero.
    if (has('leads')) {
      infinitus.push({
        type: 'dropdown',
        label: 'Comercial',
        icon: 'mat:real_estate_agent',
        children: [
          { type: 'link', label: 'Inventario', route: '/apps/infinitus/inmuebles' },
          { type: 'link', label: 'Inmuebles en captación', route: '/apps/infinitus/inmuebles-captados' },
          { type: 'link', label: 'Leads', route: '/apps/infinitus/leads' },
          // Los prospectos cuyo estudio salió no asegurable esperan una decisión
          // del comercial; sin entrada propia nadie entra a mirarlos.
          {
            type: 'link',
            label: 'Estudios no asegurables',
            route: '/apps/infinitus/leads/estudios-no-asegurables'
          },
          { type: 'link', label: 'Mapa de zonas', route: '/apps/infinitus/mapa-zonas' },
          // Entrada al informe UNIFICADO abriendo su tab comercial (semanal de
          // leads) por `fragment`. No duplica la vista: es la misma de abajo.
          {
            type: 'link',
            label: 'Informe comercial',
            route: '/apps/infinitus/informes',
            fragment: 'comercial'
          }
        ]
      });
    }
    if (has('arriendos')) {
      infinitus.push({
        type: 'dropdown',
        label: 'Arriendos',
        icon: 'mat:assignment',
        children: [
          { type: 'link', label: 'Expedientes', route: '/apps/infinitus/arriendos' },
          { type: 'link', label: 'Mis tareas', route: '/apps/infinitus/arriendos/mis-tareas' }
        ]
      });
    }
    if (has('administracion')) {
      infinitus.push({
        type: 'dropdown',
        label: 'Administración',
        icon: 'mat:business_center',
        children: [
          { type: 'link', label: 'Resumen', route: '/apps/admin/resumen' },
          { type: 'link', label: 'Terceros (SIMI)', route: '/apps/admin/terceros' },
          { type: 'link', label: 'Inmuebles (SIMI)', route: '/apps/admin/inmuebles' },
          { type: 'link', label: 'Pólizas (Libertador)', route: '/apps/admin/polizas' },
          { type: 'link', label: 'Facturación', route: '/apps/admin/facturacion' },
          { type: 'link', label: 'Cartera y cobranza', route: '/apps/admin/cartera' },
          { type: 'link', label: 'Avisos de cobranza', route: '/apps/admin/cobranza' },
          { type: 'link', label: 'Pagos al propietario', route: '/apps/admin/pagos-propietario' },
          { type: 'link', label: 'Conciliación bancaria', route: '/apps/admin/conciliacion' },
          { type: 'link', label: 'Prórrogas', route: '/apps/admin/prorrogas' },
          { type: 'link', label: 'Novedades a la aseguradora', route: '/apps/admin/novedades-aseguradora' },
          { type: 'link', label: 'Firmas', route: '/apps/infinitus/firmas' }
          // Audit log NO va acá: es el registro de quién hizo qué en TODO el
          // sistema, y eso es del administrador del sistema, no de quien
          // administra los arriendos. El backend se lo niega con 403, así que
          // ofrecerlo en el menú era prometer algo que no se puede dar
          // (decisión de David, 21/09/2026). Se movió a Sistema →
          // Configuración, dentro del bloque `esAdmin()`.
        ]
      });
    }
    // MARKETING (pedido de Andrés 26/10/2026, punto 5): la antigua «Community
    // Manager», renombrada. Suma el informe de marketing. Plantillas y Generador
    // IA se habían sacado el 28/10 y David los restauró el 30/10/2026 (vuelven a
    // listarse acá, igual que en Invering).
    if (has('community_infinitus')) {
      infinitus.push({
        type: 'dropdown',
        label: 'Marketing',
        icon: 'mat:campaign',
        children: [
          { type: 'link', label: 'Bandeja', route: '/apps/community/infinitus/bandeja' },
          { type: 'link', label: 'Campañas', route: '/apps/community/infinitus/campanas' },
          { type: 'link', label: 'Plantillas', route: '/apps/community/infinitus/plantillas' },
          { type: 'link', label: 'Generador IA', route: '/apps/community/infinitus/generador' },
          { type: 'link', label: 'Calendario', route: '/apps/community/infinitus/calendario' },
          { type: 'link', label: 'Publicaciones', route: '/apps/community/infinitus/publicaciones' },
          // Entrada al informe unificado abriendo su tab de marketing (mensual de
          // redes + reportes) por `fragment`. Misma vista que «Informe comercial».
          {
            type: 'link',
            label: 'Informe de marketing',
            route: '/apps/infinitus/informes',
            fragment: 'marketing'
          },
          { type: 'link', label: 'Tareas', route: '/apps/community/infinitus/tareas' }
        ]
      });
    }
    // El antiguo link suelto «Informes» se partió en dos entradas —«Informe
    // comercial» en Comercial e «Informe de marketing» en Marketing—, cada una
    // abriendo su tab de la MISMA vista unificada (`/apps/infinitus/informes`)
    // por `fragment`. Ver `informes-infinitus.component.ts`.
    if (infinitus.length) {
      items.push({ type: 'subheading', label: 'Infinitus', children: infinitus });
    }

    // ── Asistente ───────────────────────────────────────────────────────
    if (has('asistente')) {
      items.push({ type: 'subheading', label: 'Asistente', children: [] });
      items.push({
        // Un solo punto de entrada; al abrirlo se elige la empresa.
        // Cada opción abre el asistente dedicado en su ventana (no navega).
        type: 'dropdown',
        label: 'Asistente IA',
        icon: 'mat:auto_awesome',
        children: [
          { type: 'link', label: 'Invering', route: () => this.abrirAsistente('invering') },
          { type: 'link', label: 'Infinitus', route: () => this.abrirAsistente('infinitus') },
          { type: 'link', label: 'Mi asistente', route: '/apps/asistente/mi-asistente' }
        ]
      });
    }

    // ── Sistema · Configuración ─────────────────────────────────────────
    //
    // Agrupado en secciones desde el 03/10/2026. Antes era una lista plana de
    // 18 ítems que mezclaba cinco cosas distintas —catálogos de la empresa,
    // reglas de negocio, conexiones con terceros, el asistente y el gobierno
    // del sistema— y en la que «¿dónde configuro TikTok?» tenía dos respuestas
    // correctas en lugares distintos.
    const configuracion: Array<NavigationLink | NavigationDropdown> = [];

    if (has('configuracion')) {
      configuracion.push({
        type: 'dropdown',
        label: 'Reglas de negocio',
        children: [
          // ⚠️ Se llamaba «Cobros y arriendos» y se renombró el 12/10/2026, al
          // aparecer la pantalla de abajo. Son dos cosas distintas y el nombre
          // viejo las tapaba: ACÁ van los arriendos que ya se administran
          // (facturas, mora, prórrogas, pagos al propietario: la tabla
          // `inv_config_administracion`), y en «Expedientes de arriendo» va el
          // circuito de cierre de uno nuevo (`inv_arriendo_config`). Con los
          // dos llamándose «arriendos», «¿dónde configuro el IPC de una
          // prórroga?» tenía dos respuestas plausibles — el mismo problema que
          // tuvo TikTok antes del reagrupamiento del 03/10.
          {
            type: 'link',
            label: 'Cobros y cartera',
            route: '/apps/admin/configuracion/administracion',
          },
          {
            type: 'link',
            label: 'Tiempos de respuesta',
            route: '/apps/admin/configuracion/leads',
          },
        ],
      });
      configuracion.push({
        type: 'dropdown',
        label: 'Mi empresa',
        children: [
          { type: 'link', label: 'Áreas', route: '/apps/admin/configuracion/areas' },
          { type: 'link', label: 'Cargos', route: '/apps/admin/configuracion/cargos' },
          {
            type: 'link',
            label: 'Perfiles y competencias',
            route: '/apps/admin/configuracion/perfiles',
          },
        ],
      });
      configuracion.push({
        type: 'dropdown',
        label: 'Asistente IA',
        children: [
          { type: 'link', label: 'Matriz Q&A', route: '/apps/asistente/qa' },
          {
            type: 'link',
            label: 'Instrucciones',
            route: '/apps/asistente/personalizacion',
          },
          { type: 'link', label: 'Métricas', route: '/apps/asistente/metricas' },
        ],
      });
    }

    // Usuarios/Roles: admin-only. El backend los protege con `require_admin`
    // y `'usuarios'` ya no está en el catálogo de módulos, así que se gatean
    // por `esAdmin()` (rol con `'*'`) — no por `has('usuarios')`.
    if (this.auth.esAdmin()) {
      // ⚠️ Conexiones va PRIMERO y arranca con el resumen.
      //
      // Es lo que se busca más seguido y lo que estaba más repartido: las
      // claves de un servicio vivían en «Credenciales» y su cuenta conectada en
      // otra pantalla, así que para saber si TikTok estaba listo había que
      // visitar las dos. El resumen muestra las dos cosas en una fila.
      configuracion.unshift({
        type: 'dropdown',
        label: 'Conexiones',
        children: [
          // Una sola entrada: la pantalla tiene una pestaña por servicio, con
          // sus claves y su cuenta juntas. Las entradas sueltas de «TikTok ·
          // cuenta» y «LinkedIn · cuenta» eran justamente la mitad del
          // problema —había que saber que existían y en qué orden usarlas—,
          // así que se van. Sus rutas siguen vivas para los enlaces guardados.
          // Una sola entrada: Conexiones ahora tiene tabs por empresa con sus
          // claves y su cuenta juntas. La «Lista completa de claves» que apuntaba
          // a Credenciales se fue: esa pantalla se absorbió acá (unificación
          // 29/10/2026). Su URL redirige, así que los enlaces guardados siguen.
          {
            type: 'link',
            label: 'Servicios y cuentas',
            route: '/apps/admin/configuracion/conexiones',
          },
          {
            type: 'link',
            label: 'Publicación en redes',
            route: '/apps/admin/configuracion/community',
          },
          {
            type: 'link',
            label: 'Metrocuadrado · envío de correcciones',
            route: '/apps/admin/configuracion/metrocuadrado',
          },
        ],
      });

      const reglas = configuracion.find(
        (c) => c.type === 'dropdown' && c.label === 'Reglas de negocio',
      ) as NavigationDropdown | undefined;
      // Las plantillas de mensajes vivían en Community, una por empresa. Dejó
      // de corresponder el 02/10: ahí adentro están los textos de la cita y los
      // de cobranza, que no son de Community.
      reglas?.children.push({
        type: 'link',
        label: 'Plantillas de mensajes',
        route: '/apps/community/infinitus/plantillas',
      });
      // «Expedientes» es la palabra que esta gente ya ve en Infinitus →
      // Arriendos → Expedientes: así la pantalla de configuración se ubica sin
      // tener que adivinar a qué «arriendos» se refiere.
      reglas?.children.push({
        type: 'link',
        label: 'Expedientes de arriendo',
        route: '/apps/admin/configuracion/arriendos',
      });

      const empresa = configuracion.find(
        (c) => c.type === 'dropdown' && c.label === 'Mi empresa',
      ) as NavigationDropdown | undefined;
      empresa?.children.push({
        type: 'link',
        label: 'Contratación e inducción',
        route: '/apps/admin/configuracion/contratacion',
      });

      configuracion.push({
        type: 'dropdown',
        label: 'Sistema',
        children: [
          { type: 'link', label: 'Usuarios', route: '/apps/admin/configuracion/usuarios' },
          { type: 'link', label: 'Roles', route: '/apps/admin/configuracion/roles' },
          // Vivía en el menú de Administración (Infinitus), donde el backend le
          // respondía 403 a ese rol. Su sitio es acá: el audit log es el
          // registro de quién hizo qué en TODO el sistema.
          { type: 'link', label: 'Audit log', route: '/apps/admin/audit-log' },
          // Estaba suelto fuera del desplegable. El endpoint existe desde el
          // PR #288 y hasta hoy no tenía pantalla: la información estaba y
          // ningún humano llegaba a ella.
          {
            type: 'link',
            label: 'Avisos que no salieron',
            route: '/apps/admin/avisos-fallidos',
          },
        ],
      });
    }

    if (configuracion.length) {
      items.push({ type: 'subheading', label: 'Sistema', children: [] });
      items.push({
        type: 'dropdown',
        label: 'Configuración',
        icon: 'mat:settings',
        children: configuracion
      });
    }

    this._items.next(items);
  }
}
